import { useId, useMemo, useRef, useState } from "react";
import type {
  BuyerSequenceBlockKey,
  BuyerSequenceFieldInfoDto,
  BuyerSequenceRenderedDto,
  BuyerSequenceSelection,
  BuyerSequenceWordingBlockDto,
  BuyerSequenceWordingDto,
} from "@/api/buyerSequence";
import { extractErrorMessage } from "@/api/client";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { ConfirmModal } from "@/components/ui/PromptModals";
import {
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  Label,
  ResponsiveGrid,
  SkeletonText,
  StatusBadge,
  SubTable,
  Textarea,
  type SubTableColumn,
} from "@/design-system/components/ui";
import { cn } from "@/design-system/lib/utils";
import { useDateFormatter } from "@/utils/datetime";
import { TemplateChips } from "./TemplateChips";
import { TeamChangedNotice } from "./TeamChangedNotice";
import { fieldTag, insertAt } from "./templateTokens";
import {
  personalBaseline,
  teamBaseline,
  useDeletePersonal,
  useDiscardTeamDraft,
  usePublishTeam,
  useRollbackTeam,
  useSavePersonal,
  useSaveTeamDraft,
  useWording,
  useWordingPreview,
  useWorkingBlocks,
} from "./useWording";

export type WordingMode = "team" | "mine";

export interface WordingTemplateEditorProps {
  buyerId: string;
  templateKey: string;
  mode: WordingMode;
  /** The blocks this view edits. Every block of the template is still held and
   *  saved; this only chooses which are drawn. */
  visibleBlocks: readonly BuyerSequenceBlockKey[];
  selection: BuyerSequenceSelection;
  fields: readonly BuyerSequenceFieldInfoDto[] | undefined;
  heading: string;
  description?: string;
  /** The signature has its own save and reset, named for it. */
  isSignature?: boolean;
  /** Kept mounted while hidden so unsaved text survives a Text/Email switch. */
  hidden?: boolean;
}

const BLOCK_ROWS: Record<string, number> = { text: 6, subject: 2, email: 10, signature: 4 };

/**
 * One outreach template — a step, or the signature — in Edit wording.
 *
 * Holds TWO working copies, team and personal, so switching between them keeps
 * what was typed in each. Every block of the template is held even when the view
 * shows only some of them: a personal version is saved WHOLE, so "Save my
 * version" from the Email view still carries the text message as edited.
 *
 * A save the server refuses shows the server's own words and changes nothing:
 * the working text is left exactly as typed.
 */
export function WordingTemplateEditor({
  buyerId,
  templateKey,
  mode,
  visibleBlocks,
  selection,
  fields,
  heading,
  description,
  isSignature = false,
  hidden = false,
}: WordingTemplateEditorProps) {
  const q = useWording(templateKey);
  const wording = q.data;
  const { date, dateTime } = useDateFormatter();

  const team = useWorkingBlocks(useMemo(() => (wording ? teamBaseline(wording) : null), [wording]));
  const mine = useWorkingBlocks(
    useMemo(() => (wording ? personalBaseline(wording) : null), [wording]),
  );

  const saveDraft = useSaveTeamDraft(templateKey);
  const discardDraft = useDiscardTeamDraft(templateKey);
  const publish = usePublishTeam(templateKey);
  const rollback = useRollbackTeam(templateKey);
  const savePersonal = useSavePersonal(templateKey);
  const deletePersonal = useDeletePersonal(templateKey);

  const [actionError, setActionError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<
    null | { kind: "discard" } | { kind: "reset" } | { kind: "rollback"; version: number }
  >(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishNote, setPublishNote] = useState("");
  const [publishError, setPublishError] = useState<string | null>(null);

  const canEditTeam = wording?.canEditTeam ?? false;
  const editingTeam = mode === "team";
  const readOnly = editingTeam && !canEditTeam;
  const copy = editingTeam ? team : mine;

  // The preview renders the wording as it would go out from THIS mode: the text
  // being edited, or for someone who cannot edit the team default, the PUBLISHED
  // team text they are reading — never a draft they cannot see.
  const publishedTeam = useMemo(
    () => (wording ? Object.fromEntries(wording.blocks.map((b) => [b.blockKey, b.teamText])) : null),
    [wording],
  );
  const previewBlocks = readOnly ? publishedTeam : copy.working;
  const preview = useWordingPreview({
    buyerId,
    templateKey,
    blocks: previewBlocks,
    selection,
    enabled: !hidden && !!wording,
  });

  const shownBlocks = useMemo(
    () => (wording?.blocks ?? []).filter((b) => visibleBlocks.includes(b.blockKey)),
    [wording, visibleBlocks],
  );
  const labelOf = (blockKey: string) =>
    wording?.blocks.find((b) => b.blockKey === blockKey)?.label ?? blockKey;

  const handlers = {
    onSuccess: () => setActionError(null),
    onError: (e: unknown) => setActionError(extractErrorMessage(e)),
  };

  const noun = isSignature ? "signature" : "version";
  // The `hidden` attribute alone loses to the `flex` utility (same specificity,
  // utilities load later), so the class carries it too.
  const cardClass = cn("flex flex-col gap-space-3", hidden && "hidden");

  return (
    <Card
      hidden={hidden}
      data-testid={`wording-editor-${isSignature ? "signature" : "step"}`}
      className={cardClass}
    >
      <CardHeader className="mb-0">
        <div className="flex flex-wrap items-center justify-between gap-space-2">
          <CardTitle className="text-lead">{heading}</CardTitle>
          {wording && (
            <div className="flex flex-wrap items-center gap-space-2">
              <StatusBadge tone="muted">
                {wording.teamVersion === 0
                  ? "Team: code default"
                  : `Team: version ${wording.teamVersion}`}
              </StatusBadge>
              {editingTeam && wording.hasTeamDraft && (
                <StatusBadge tone="warn">Unpublished draft</StatusBadge>
              )}
              {!editingTeam && wording.hasPersonal && (
                <StatusBadge tone="progress">Your version in use</StatusBadge>
              )}
            </div>
          )}
        </div>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>

      {q.isLoading ? (
        <SkeletonText lines={4} />
      ) : q.isError || !wording ? (
        <div className="flex flex-wrap items-center gap-space-2" role="alert">
          <span className="text-small text-status-error">
            Couldn't load this wording. {extractErrorMessage(q.error)}
          </span>
          <Button size="sm" variant="secondary" onClick={() => void q.refetch()}>
            Retry
          </Button>
        </div>
      ) : (
        <>
          {!editingTeam && <TeamChangedNotice buyerId={buyerId} templateKey={templateKey} />}

          <ResponsiveGrid cols={2} minColWidth="22rem">
            <div className="flex min-w-0 flex-col gap-space-4">
              {readOnly && (
                <p className="text-small text-gray-mid" data-testid="wording-team-readonly">
                  Only template editors change the team default. Switch to My version to
                  make your own.
                </p>
              )}

              {shownBlocks.map((b) =>
                readOnly ? (
                  <ReadOnlyBlock key={b.blockKey} block={b} text={b.teamText} fields={fields} />
                ) : (
                  <BlockEditor
                    key={b.blockKey}
                    block={b}
                    value={copy.working?.[b.blockKey] ?? ""}
                    dirty={copy.dirtyKeys.includes(b.blockKey)}
                    fields={fields}
                    onChange={(v) => copy.setBlock(b.blockKey, v)}
                    draftAction={
                      editingTeam
                        ? {
                            hasDraft: b.teamDraftText != null,
                            pending:
                              saveDraft.isPending && saveDraft.variables?.blockKey === b.blockKey,
                            onSave: () =>
                              saveDraft.mutate(
                                { blockKey: b.blockKey, text: team.working?.[b.blockKey] ?? "" },
                                handlers,
                              ),
                          }
                        : undefined
                    }
                  />
                ),
              )}

              {editingTeam && canEditTeam && (
                <div className="flex flex-col gap-space-2" data-testid="wording-team-controls">
                  {team.dirtyKeys.length > 0 && (
                    <p className="text-caption text-gray-mid">
                      Save each changed block as a draft before publishing.
                    </p>
                  )}
                  <div className="flex flex-wrap items-center gap-space-2">
                    <Button
                      size="sm"
                      disabled={!wording.hasTeamDraft || team.dirtyKeys.length > 0}
                      onClick={() => {
                        setPublishNote("");
                        setPublishError(null);
                        setPublishOpen(true);
                      }}
                    >
                      Publish
                    </Button>
                    {wording.hasTeamDraft && (
                      <Button
                        size="sm"
                        variant="secondary"
                        loading={discardDraft.isPending}
                        onClick={() => setConfirm({ kind: "discard" })}
                      >
                        Discard draft
                      </Button>
                    )}
                  </div>
                  {!wording.hasTeamDraft && team.dirtyKeys.length === 0 && (
                    <p className="text-caption text-gray-mid">
                      No draft. Edit a block and save it as a draft to publish a new team
                      version.
                    </p>
                  )}
                </div>
              )}

              {!editingTeam && (
                <div className="flex flex-col gap-space-2" data-testid="wording-personal-controls">
                  <p className="text-caption text-onyx">
                    {wording.hasPersonal
                      ? `You're using your own ${noun}${
                          wording.personalSavedAtUtc
                            ? `, saved ${date(wording.personalSavedAtUtc)}`
                            : ""
                        }.`
                      : `You're using the team default. Saving makes your own ${noun}; the team default does not change.`}
                  </p>
                  <div className="flex flex-wrap items-center gap-space-2">
                    <Button
                      size="sm"
                      disabled={mine.dirtyKeys.length === 0}
                      loading={savePersonal.isPending}
                      onClick={() => {
                        if (!mine.working) return;
                        savePersonal.mutate({ ...mine.working }, handlers);
                      }}
                    >
                      {isSignature ? "Save my signature" : "Save my version"}
                    </Button>
                    {mine.dirtyKeys.length > 0 && (
                      <Button size="sm" variant="text" onClick={() => mine.reset()}>
                        Undo my changes
                      </Button>
                    )}
                    {wording.hasPersonal && (
                      <Button
                        size="sm"
                        variant="secondary"
                        loading={deletePersonal.isPending}
                        onClick={() => setConfirm({ kind: "reset" })}
                      >
                        {isSignature ? "Reset signature to team default" : "Reset to team default"}
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {actionError && (
                <p
                  role="alert"
                  className="rounded-md border border-status-error bg-status-error/12 p-space-2 text-small text-status-error"
                  data-testid="wording-action-error"
                >
                  {actionError}
                </p>
              )}
            </div>

            <PreviewPane
              blockKeys={shownBlocks.map((b) => b.blockKey)}
              labelOf={labelOf}
              fields={fields}
              result={preview.data}
              loading={preview.isLoading}
              updating={preview.isFetching && !preview.isLoading}
              error={preview.isError ? extractErrorMessage(preview.error) : null}
              onRetry={() => void preview.refetch()}
            />
          </ResponsiveGrid>

          {editingTeam && canEditTeam && (
            <VersionsTable
              wording={wording}
              formatWhen={(iso) => dateTime(iso)}
              pending={rollback.isPending}
              onRollback={(version) => setConfirm({ kind: "rollback", version })}
            />
          )}
        </>
      )}

      <ConfirmModal
        open={confirm?.kind === "discard"}
        title="Discard the team draft?"
        message="Every unpublished change to this template's team default is removed. The published wording stays as it is."
        confirmLabel="Discard draft"
        destructive
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          setConfirm(null);
          discardDraft.mutate(undefined, {
            ...handlers,
            onSuccess: () => {
              setActionError(null);
              team.reset();
            },
          });
        }}
      />
      <ConfirmModal
        open={confirm?.kind === "reset"}
        title={isSignature ? "Reset your signature?" : "Reset to the team default?"}
        message={`Your own ${noun} is deleted, and your messages use the team default again. Nothing you have already logged changes.`}
        confirmLabel="Reset to team default"
        destructive
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          setConfirm(null);
          deletePersonal.mutate(undefined, {
            ...handlers,
            onSuccess: () => {
              setActionError(null);
              mine.reset();
            },
          });
        }}
      />
      <ConfirmModal
        open={confirm?.kind === "rollback"}
        title="Roll back the team default?"
        message={
          confirm?.kind === "rollback"
            ? confirm.version === 0
              ? "The original wording is published again as a new team version. Everyone without their own version uses it straight away."
              : `Version ${confirm.version}'s wording is published again as a new team version. Everyone without their own version uses it straight away.`
            : ""
        }
        confirmLabel="Roll back"
        destructive
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          if (confirm?.kind !== "rollback") return;
          const version = confirm.version;
          setConfirm(null);
          rollback.mutate(version, {
            ...handlers,
            onSuccess: () => {
              setActionError(null);
              team.reset();
            },
          });
        }}
      />
      <Modal
        open={publishOpen}
        onClose={() => setPublishOpen(false)}
        title="Publish the team default"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setPublishOpen(false)}>
              Cancel
            </Button>
            <Button
              loading={publish.isPending}
              onClick={() => {
                const note = publishNote.trim();
                publish.mutate(note === "" ? null : note, {
                  onSuccess: () => {
                    setPublishOpen(false);
                    setActionError(null);
                  },
                  onError: (e) => setPublishError(extractErrorMessage(e)),
                });
              }}
            >
              Publish
            </Button>
          </>
        }
      >
        <PublishBody
          note={publishNote}
          onNote={setPublishNote}
          error={publishError}
        />
      </Modal>
    </Card>
  );
}

// ── A block ──────────────────────────────────────────────────────────────────

function BlockEditor({
  block,
  value,
  dirty,
  fields,
  onChange,
  draftAction,
}: {
  block: BuyerSequenceWordingBlockDto;
  value: string;
  dirty: boolean;
  fields: readonly BuyerSequenceFieldInfoDto[] | undefined;
  onChange: (v: string) => void;
  draftAction?: { hasDraft: boolean; pending: boolean; onSave: () => void };
}) {
  const id = useId();
  const ref = useRef<HTMLTextAreaElement>(null);
  const insertable = (fields ?? []).filter((f) => !f.isFlag);

  const insertField = (name: string) => {
    const ta = ref.current;
    const { text, caret } = insertAt(value, fieldTag(name), ta?.selectionStart, ta?.selectionEnd);
    onChange(text);
    requestAnimationFrame(() => {
      if (!ta) return;
      ta.focus();
      ta.setSelectionRange(caret, caret);
    });
  };

  return (
    <div className="flex flex-col gap-space-2" data-testid={`wording-block-${block.blockKey}`}>
      <div className="flex flex-wrap items-center justify-between gap-space-2">
        <Label htmlFor={id}>{block.label}</Label>
        <div className="flex flex-wrap items-center gap-space-2">
          {draftAction?.hasDraft && <StatusBadge tone="warn">Draft</StatusBadge>}
          {dirty && <StatusBadge tone="warn">Unsaved</StatusBadge>}
        </div>
      </div>
      <Textarea
        id={id}
        ref={ref}
        rows={BLOCK_ROWS[block.blockKey] ?? 4}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="font-mono"
      />
      <div className="flex flex-wrap items-center gap-space-2">
        <Select
          aria-label={`Insert field into ${block.label}`}
          className="h-9 w-auto max-w-full text-small"
          value=""
          disabled={insertable.length === 0}
          onChange={(e) => {
            if (e.target.value) insertField(e.target.value);
          }}
        >
          <option value="">Insert field…</option>
          {insertable.map((f) => (
            <option key={f.name} value={f.name}>
              {f.label}
            </option>
          ))}
        </Select>
        {draftAction && (
          <Button
            size="sm"
            variant="secondary"
            disabled={!dirty}
            loading={draftAction.pending}
            onClick={draftAction.onSave}
          >
            Save draft
          </Button>
        )}
      </div>
      <div className="rounded-md border border-border-light bg-white-warm p-space-2">
        <span className="mb-space-1 block text-caption font-semibold text-gray-mid">Reads as</span>
        <TemplateChips
          text={value}
          fields={fields}
          data-testid={`wording-chips-${block.blockKey}`}
        />
      </div>
    </div>
  );
}

function ReadOnlyBlock({
  block,
  text,
  fields,
}: {
  block: BuyerSequenceWordingBlockDto;
  text: string;
  fields: readonly BuyerSequenceFieldInfoDto[] | undefined;
}) {
  return (
    <div className="flex flex-col gap-space-1" data-testid={`wording-block-${block.blockKey}`}>
      <span className="text-small font-semibold text-onyx">{block.label}</span>
      <div className="rounded-md border border-border-light bg-white-warm p-space-2">
        <TemplateChips text={text} fields={fields} data-testid={`wording-chips-${block.blockKey}`} />
      </div>
    </div>
  );
}

// ── Preview ──────────────────────────────────────────────────────────────────

function PreviewPane({
  blockKeys,
  labelOf,
  fields,
  result,
  loading,
  updating,
  error,
  onRetry,
}: {
  blockKeys: string[];
  labelOf: (blockKey: string) => string;
  fields: readonly BuyerSequenceFieldInfoDto[] | undefined;
  result: { blocks: Record<string, BuyerSequenceRenderedDto>; findings: { blockKey: string; message: string }[] } | undefined;
  loading: boolean;
  updating: boolean;
  error: string | null;
  onRetry: () => void;
}) {
  const fieldLabel = (name: string) => fields?.find((f) => f.name === name)?.label ?? name;

  return (
    <section
      className="flex min-w-0 flex-col gap-space-3 rounded-md border border-border-light p-space-3"
      aria-label="Preview on this buyer"
      data-testid="wording-preview"
    >
      <div className="flex items-center justify-between gap-space-2">
        <h4 className="font-display text-small font-bold text-onyx">Preview on this buyer</h4>
        {updating && <span className="text-caption text-gray-mid">Updating…</span>}
      </div>

      {loading ? (
        <SkeletonText lines={4} />
      ) : error && !result ? (
        <div className="flex flex-wrap items-center gap-space-2">
          <span className="text-small text-status-error">Preview unavailable. {error}</span>
          <Button size="sm" variant="secondary" onClick={onRetry}>
            Retry
          </Button>
        </div>
      ) : result ? (
        <>
          {blockKeys.map((key) => {
            const rendered = result.blocks[key];
            return (
              <div key={key} className="flex flex-col gap-space-1" data-testid={`wording-preview-${key}`}>
                <span className="text-caption font-semibold uppercase tracking-wide text-gray-mid">
                  {labelOf(key)}
                </span>
                {rendered ? (
                  <p className="whitespace-pre-wrap break-words text-small text-onyx">
                    {rendered.segments.map((s, i) =>
                      s.field ? (
                        <span
                          key={i}
                          data-field={s.field}
                          data-missing={s.missing ? "true" : undefined}
                          title={fieldLabel(s.field)}
                          className={cn(
                            "rounded-sm px-[2px]",
                            s.missing
                              ? "bg-status-error/12 text-status-error"
                              : "bg-gold/15 text-onyx",
                          )}
                        >
                          {s.text}
                        </span>
                      ) : (
                        <span key={i}>{s.text}</span>
                      ),
                    )}
                  </p>
                ) : (
                  <p className="text-small text-gray-mid">Nothing to show.</p>
                )}
                {rendered && rendered.missingRequiredFields.length > 0 && (
                  <p className="text-caption text-status-error">
                    Needs a value before it can be copied:{" "}
                    {rendered.missingRequiredFields.map(fieldLabel).join(", ")}
                  </p>
                )}
              </div>
            );
          })}

          {result.findings.length > 0 ? (
            <div className="flex flex-col gap-space-1" data-testid="wording-findings">
              <span className="text-small font-semibold text-status-error">
                Problems to fix before this can be saved
              </span>
              <ul className="flex list-disc flex-col gap-[2px] pl-space-5">
                {result.findings.map((f, i) => (
                  <li key={i} className="text-caption text-onyx">
                    <span className="font-semibold">{labelOf(f.blockKey)}:</span> {f.message}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-caption text-gray-mid">No problems found.</p>
          )}
        </>
      ) : null}
    </section>
  );
}

// ── Team versions ────────────────────────────────────────────────────────────

interface VersionRow {
  version: number;
  publishedAtUtc: string | null;
  publishedByName: string | null;
  note: string | null;
}

function VersionsTable({
  wording,
  formatWhen,
  pending,
  onRollback,
}: {
  wording: BuyerSequenceWordingDto;
  formatWhen: (iso: string) => string;
  pending: boolean;
  onRollback: (version: number) => void;
}) {
  const rows: VersionRow[] = [...wording.teamVersions]
    .sort((a, b) => b.version - a.version)
    .map((v) => ({ ...v }));
  // Version 0 is the code default. Offered as a rollback target only once
  // something has replaced it.
  if (wording.teamVersion !== 0) {
    rows.push({ version: 0, publishedAtUtc: null, publishedByName: null, note: null });
  }

  const columns: SubTableColumn<VersionRow>[] = [
    {
      key: "version",
      header: "Version",
      render: (r) => (
        <span className="inline-flex flex-wrap items-center gap-space-1">
          {r.version === 0 ? "Code default" : `Version ${r.version}`}
          {r.version === wording.teamVersion && <StatusBadge tone="ok">In use</StatusBadge>}
        </span>
      ),
      sortAccessor: (r) => r.version,
    },
    {
      key: "published",
      header: "Published",
      render: (r) =>
        r.publishedAtUtc ? formatWhen(r.publishedAtUtc) : <span className="text-gray-mid">Built in</span>,
    },
    {
      key: "by",
      header: "By",
      render: (r) => r.publishedByName ?? <span className="text-gray-mid">—</span>,
    },
    {
      key: "note",
      header: "Note",
      render: (r) => r.note ?? <span className="text-gray-mid">—</span>,
    },
    {
      key: "action",
      header: <span className="sr-only">Action</span>,
      render: (r) =>
        r.version === wording.teamVersion ? null : (
          <Button
            size="sm"
            variant="text"
            disabled={pending}
            onClick={() => onRollback(r.version)}
            aria-label={
              r.version === 0 ? "Roll back to the code default" : `Roll back to version ${r.version}`
            }
          >
            Roll back to this
          </Button>
        ),
    },
  ];

  return (
    <div className="flex flex-col gap-space-2" data-testid="wording-versions">
      <h4 className="font-display text-small font-bold text-onyx">Published team versions</h4>
      <SubTable
        caption="Published team versions"
        columns={columns}
        rows={rows}
        rowKey={(r) => String(r.version)}
        rowTestId={(r) => `wording-version-${r.version}`}
        empty={{ message: "Nothing published yet. The code default is in use." }}
      />
    </div>
  );
}

function PublishBody({
  note,
  onNote,
  error,
}: {
  note: string;
  onNote: (v: string) => void;
  error: string | null;
}) {
  const id = useId();
  return (
    <div className="flex flex-col gap-space-2">
      <p className="text-small text-onyx">
        The draft becomes the team default straight away for everyone without their own
        version. People with their own version are told the team default changed.
      </p>
      <Label htmlFor={id}>Note (optional)</Label>
      <Textarea id={id} rows={3} value={note} onChange={(e) => onNote(e.target.value)} />
      {error && (
        <p role="alert" className="text-caption text-status-error">
          {error}
        </p>
      )}
    </div>
  );
}
